import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import type { QBEntityListResponse, QBEntityRow } from "@/lib/types/quickbooks-unified";

export type { AdminQBCustomerRow } from "@/lib/types/quickbooks-unified";

export function useAdminQuickBooksEntitiesByRealm(params: {
  realmId: string;
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  entityType?: "customers" | "invoices";
  enabled?: boolean;
}) {
  const {
    realmId,
    page,
    pageSize,
    search = "",
    status = "all",
    entityType = "customers",
    enabled = true,
  } = params;
  const key = [
    "admin-qb-entities-by-realm",
    realmId,
    { page, pageSize, search, status, entityType },
  ];
  const { session } = useUniversalAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const sp = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        search,
        status,
        entityType,
        realmId,
      });
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`/api/quickbooks/entities?${sp.toString()}`, {
        headers,
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load QuickBooks ${entityType} for realm ${realmId}`);
      return res.json() as Promise<QBEntityListResponse>;
    },
    placeholderData: keepPreviousData,
    enabled: enabled && !!realmId,
  });
}

export function useAdminQuickBooksEntitiesMultiRealm(params: {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  entityType?: "customers" | "invoices";
  enabled?: boolean;
  realmIds?: string[];
}) {
  const {
    page,
    pageSize,
    search = "",
    status = "all",
    entityType = "customers",
    enabled = true,
    realmIds = [],
  } = params;
  const key = [
    "admin-qb-entities-multi-realm",
    realmIds,
    { page, pageSize, search, status, entityType },
  ];
  const { session } = useUniversalAuth();
  const token = session?.access_token;

  return useQuery({
    queryKey: key,
    queryFn: async () => {
      if (realmIds.length === 0) {
        return { data: [], page, pageSize, total: 0, totalPages: 1, entityType };
      }

      const promises = realmIds.map(async (realmId) => {
        const sp = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
          search,
          status,
          entityType,
          realmId,
        });
        const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`/api/quickbooks/entities?${sp.toString()}`, {
          headers,
          credentials: "include",
        });
        if (!res.ok) {
          logger.warn(`Failed to load QuickBooks ${entityType} for realm ${realmId}`, {
            status: res.status,
          });
          return { data: [], page, pageSize, total: 0, totalPages: 1, entityType, realmId };
        }
        const data = (await res.json()) as QBEntityListResponse;
        return { ...data, realmId };
      });

      const results = await Promise.all(promises);
      const allData: QBEntityRow[] = results.flatMap((result) => result.data || []);
      const totalCount = results.reduce((sum, result) => sum + (result.total || 0), 0);

      return {
        data: allData,
        page,
        pageSize,
        total: totalCount,
        totalPages: Math.ceil(totalCount / pageSize) || 1,
        entityType,
        realmResults: results,
      };
    },
    placeholderData: keepPreviousData,
    enabled: enabled && realmIds.length > 0,
  });
}

export function useAdminQuickBooksEntities(params: {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  entityType?: "customers" | "invoices";
  enabled?: boolean;
}) {
  const {
    page,
    pageSize,
    search = "",
    status = "all",
    entityType = "customers",
    enabled = true,
  } = params;
  const key = ["admin-qb-entities", { page, pageSize, search, status, entityType }];
  const { session } = useUniversalAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const sp = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        search,
        status,
        entityType,
      });
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`/api/quickbooks/entities?${sp.toString()}`, {
        headers,
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load QuickBooks ${entityType}`);
      return res.json() as Promise<QBEntityListResponse>;
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useAdminQuickBooksCustomers(params: {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  enabled?: boolean;
}) {
  return useAdminQuickBooksEntities({ ...params, entityType: "customers" });
}

export function useMatchQBCustomer() {
  const qc = useQueryClient();
  const { session } = useUniversalAuth();
  return useMutation({
    mutationFn: async (payload: {
      qbId: string;
      contactId?: string;
      companyId?: string;
      quickbooksDisplayName?: string;
      quickbooksRealmId?: string;
      bulkCompanyName?: string;
      bulkContactIds?: string[];
    }) => {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(`/api/quickbooks/entities/${payload.qbId}?entityType=customer`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          contactId: payload.contactId,
          companyId: payload.companyId,
          quickbooksDisplayName: payload.quickbooksDisplayName,
          quickbooksRealmId: payload.quickbooksRealmId,
          bulkCompanyName: payload.bulkCompanyName,
          bulkContactIds: payload.bulkContactIds,
        }),
      });
      if (!res.ok) {
        let errorMessage = "Failed to match customer";
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = res.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      return res.json();
    },
    onSuccess: async (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ["admin-qb-customers"] });
      try {
        const msg =
          variables.bulkContactIds && variables.bulkContactIds.length > 0
            ? `Matched ${variables.bulkContactIds.length} contacts to QuickBooks`
            : variables.bulkCompanyName
              ? `Matched all contacts in "${variables.bulkCompanyName}" to QuickBooks`
              : "QuickBooks match saved";
        const { toast } = await import("sonner");
        toast.success(msg);
      } catch {}
    },
    onError: async (error: unknown) => {
      try {
        const { toast } = await import("sonner");
        toast.error(error instanceof Error ? error.message : "Failed to save QuickBooks match");
      } catch {}
    },
  });
}

export function useRemoveQBCustomerMapping() {
  const qc = useQueryClient();
  const { session } = useUniversalAuth();

  return useMutation({
    mutationFn: async (payload: {
      contactId?: string;
      companyId?: string;
      qbCustomerId: string;
    }) => {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(
        `/api/quickbooks/entities/${payload.qbCustomerId}?entityType=customer`,
        {
          method: "DELETE",
          headers,
          credentials: "include",
          body: JSON.stringify({ contactId: payload.contactId, companyId: payload.companyId }),
        }
      );
      if (!res.ok) throw new Error("Failed to remove mapping");
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      void qc.invalidateQueries({ queryKey: ["admin-qb-customers"] });
      toast.success("QuickBooks mapping removed successfully");
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Failed to remove QuickBooks mapping");
    },
  });
}
